import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useWebSocket } from './WebSocketContext';

interface TokenBalance {
  balance: number;
  freeMonthlyAllowance: number;
  freeTokensRemaining: number;
  freeAllowanceResetDate: string;
  totalPurchased: number;
  totalUsed: number;
  autoReload: {
    enabled: boolean;
    thresholdTokens: number;
    reloadPackId?: string;
    stripePaymentMethodId?: string;
  };
}

interface TokenContextType {
  balance: number;
  freeMonthlyAllowance: number;
  freeTokensRemaining: number;
  totalPurchased: number;
  totalUsed: number;
  isLoading: boolean;
  isOutOfTokens: boolean;
  isPurchaseModalOpen: boolean;
  autoReload: TokenBalance['autoReload'];
  openPurchaseModal: () => void;
  closePurchaseModal: () => void;
  refreshBalance: () => Promise<void>;
}

const TokenContext = createContext<TokenContextType | undefined>(undefined);

export const useToken = () => {
  const context = useContext(TokenContext);
  if (!context) {
    throw new Error('useToken must be used within a TokenProvider');
  }
  return context;
};

export const TokenProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { socket } = useWebSocket();
  const [balance, setBalance] = useState(0);
  const [freeMonthlyAllowance, setFreeMonthlyAllowance] = useState(0);
  const [freeTokensRemaining, setFreeTokensRemaining] = useState(0);
  const [totalPurchased, setTotalPurchased] = useState(0);
  const [totalUsed, setTotalUsed] = useState(0);
  const [autoReload, setAutoReload] = useState<TokenBalance['autoReload']>({
    enabled: false,
    thresholdTokens: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);

  const isOutOfTokens = balance <= 0 && freeTokensRemaining <= 0;

  const fetchBalance = useCallback(async () => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/tokens/balance`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setBalance(data.data.balance);
          setFreeMonthlyAllowance(data.data.freeMonthlyAllowance);
          setFreeTokensRemaining(data.data.freeTokensRemaining);
          setTotalPurchased(data.data.totalPurchased);
          setTotalUsed(data.data.totalUsed);
          setAutoReload(data.data.autoReload || { enabled: false, thresholdTokens: 0 });
        }
      }
    } catch (error) {
      console.error('Failed to fetch token balance:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const refreshBalance = useCallback(async () => {
    await fetchBalance();
  }, [fetchBalance]);

  // Fetch balance on mount and when authentication changes
  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // Listen to WebSocket events for real-time balance updates
  useEffect(() => {
    if (!socket) return;

    const handleTokensUpdated = (data: TokenBalance) => {
      setBalance(data.balance);
      setFreeMonthlyAllowance(data.freeMonthlyAllowance);
      setFreeTokensRemaining(data.freeTokensRemaining);
      setTotalPurchased(data.totalPurchased);
      setTotalUsed(data.totalUsed);
    };

    socket.on('tokens:updated', handleTokensUpdated);

    return () => {
      socket.off('tokens:updated', handleTokensUpdated);
    };
  }, [socket]);

  // Listen to X-Token-Balance response headers (if available via interceptor)
  // This would require a fetch interceptor - for now, we'll rely on WebSocket and manual refresh

  const openPurchaseModal = useCallback(() => {
    setIsPurchaseModalOpen(true);
  }, []);

  const closePurchaseModal = useCallback(() => {
    setIsPurchaseModalOpen(false);
  }, []);

  return (
    <TokenContext.Provider
      value={{
        balance,
        freeMonthlyAllowance,
        freeTokensRemaining,
        totalPurchased,
        totalUsed,
        isLoading,
        isOutOfTokens,
        isPurchaseModalOpen,
        autoReload,
        openPurchaseModal,
        closePurchaseModal,
        refreshBalance,
      }}
    >
      {children}
    </TokenContext.Provider>
  );
};
